import {
  Organization,
  PersistentHeader,
  PersistentHeaderRenderArgs,
} from "@deere/persistent-header";
import React, { FC, useEffect, useRef } from "react";
import { ThemeProvider, Themes } from "@deere/isg.component-library";
import { AppContext } from "../context/AppContext";
import { Socket } from "socket.io-client";
import { User } from "../../types";

type AppHeaderProps = {
  children: React.ReactElement;
  persistentHeaderUrl: string;
  userId: string;
  socket: Socket | null;
  latency: number;
  user: User;
};

// POST the user's persistent-header org list to the backend so it can
// populate `req.session.user.allowedOrgs`. The backend's `getAllowedOrgNames`
// call against sandboxapi.deere.com is rejected for this app's Okta token
// (custom auth server, eq2 scope), so the persistent-header — which uses
// the user's Deere SSO cookies — is the practical source of truth.
const syncOrgsToBackend = async (organizations: Organization[]): Promise<void> => {
  try {
    const response = await fetch("/API/auth/orgs/sync", {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        organizations: organizations.map((o) => ({
          id: o.id,
          name: o.name,
          isMember: o.isMember,
        })),
      }),
    });
    if (!response.ok) {
      console.warn(
        `[AppHeader] /auth/orgs/sync returned ${response.status} ${response.statusText}`,
      );
    }
  } catch (err) {
    console.warn("[AppHeader] /auth/orgs/sync failed:", err);
  }
};

export const AppHeader: FC<AppHeaderProps> = (props: AppHeaderProps) => {
  const [selectedOrganization, setSelectedOrganization] = React.useState<
    Organization | undefined
  >();
  const [mqttData, setMqttData] = React.useState<any>({});
  // Avoid re-POSTing the same org list on every persistent-header data
  // event (the iframe fires onData repeatedly). Key on org id+name pairs.
  const lastSyncedOrgsKey = useRef<string>("");

  // Suppress cross-origin SecurityErrors thrown internally by the PersistentHeader
  // iframe when it attempts to read window.fetchingToken across origins.
  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      if (
        event.error instanceof DOMException &&
        event.error.name === "SecurityError" &&
        event.message?.includes("fetchingToken")
      ) {
        event.preventDefault();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  const onDataHandler = async (
    persistentHeaderRenderArgs: PersistentHeaderRenderArgs,
  ): Promise<void> => {
    const hierarchy = persistentHeaderRenderArgs?.organizationHierarchy;
    const newOrg = persistentHeaderRenderArgs?.selectedOrganizations?.[0];

    // Sync the org allowlist to the backend BEFORE propagating the
    // selected org. Otherwise the downstream useEffect that emits
    // `requestInitialData` over the socket can race the sync POST, the
    // backend sees an empty allowedOrgs, and the request is denied with
    // a permissions error for an org the user actually belongs to.
    if (Array.isArray(hierarchy) && hierarchy.length > 0) {
      const key = hierarchy
        .map((o) => `${o.id ?? ""}:${o.name ?? ""}:${o.isMember ? 1 : 0}`)
        .sort()
        .join("|");
      if (key !== lastSyncedOrgsKey.current) {
        lastSyncedOrgsKey.current = key;
        await syncOrgsToBackend(hierarchy);
      }
    }

    if (newOrg) {
      setSelectedOrganization((prev) => {
        if (prev?.name === newOrg?.name) return prev;
        return newOrg;
      });
    }
  };
  return (
    <PersistentHeader
      multiSelect={false}
      url={props.persistentHeaderUrl}
      onData={onDataHandler}
      userId={props.userId}
    >
      <AppContext.Provider
        value={{
          selectedOrganization,
          socket: props.socket,
          latency: props.latency,
          user: props.user,
        }}
      >
        <ThemeProvider theme={Themes.jddcConstruction}>{props.children}</ThemeProvider>
      </AppContext.Provider>
    </PersistentHeader>
  );
};
